import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, Loader2, Info } from "lucide-react";
import { GRADES } from "@/lib/constants";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface AIPracticeTestRequest {
  grade: string;
  subjectId: string;
  subjectName: string;
  categoryId: string | null;
  categoryName: string | null;
  topicTitle: string | null;
  focusHint: string | null;
  count: number;
  difficulty: "easy" | "medium" | "hard";
}

interface Props {
  defaultGrade?: string;
  defaultSubjectId?: string;
  defaultCategoryId?: string;
  loading?: boolean;
  onCancel?: () => void;
  onSubmit: (req: AIPracticeTestRequest) => void;
}

const DIFFICULTIES: Array<{ id: "easy" | "medium" | "hard"; label: string }> = [
  { id: "easy", label: "Easy" },
  { id: "medium", label: "Medium" },
  { id: "hard", label: "Hard" },
];

export default function AIPracticeTestSetup({
  defaultGrade,
  defaultSubjectId,
  defaultCategoryId,
  loading,
  onCancel,
  onSubmit,
}: Props) {
  const { user } = useAuth();
  const [grade, setGrade] = useState<string>(defaultGrade ?? "");
  const [subjectId, setSubjectId] = useState<string>(defaultSubjectId ?? "");
  const [categoryId, setCategoryId] = useState<string>(defaultCategoryId ?? "");
  const [topicId, setTopicId] = useState<string>("");
  const [focusHint, setFocusHint] = useState<string>("");
  const [count, setCount] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">("medium");

  // Subjects for selected grade
  const subjectsQuery = useQuery({
    queryKey: ["ai-test-subjects", grade],
    enabled: !!grade,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects")
        .select("id, name")
        .eq("grade", grade)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Categories for selected subject
  const categoriesQuery = useQuery({
    queryKey: ["ai-test-categories", subjectId],
    enabled: !!subjectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects_categories")
        .select("id, name")
        .eq("subject_id", subjectId)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Topics for selected category
  const topicsQuery = useQuery({
    queryKey: ["ai-test-topics", subjectId, categoryId],
    enabled: !!subjectId && !!categoryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topics")
        .select("id, title")
        .eq("subject_id", subjectId)
        .eq("category_id", categoryId)
        .order("title");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Reset child selects when parent changes (only when not matching prefilled defaults)
  useEffect(() => {
    if (defaultSubjectId && subjectId === defaultSubjectId) return;
    setSubjectId("");
    setCategoryId("");
    setTopicId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade]);
  useEffect(() => {
    if (defaultCategoryId && categoryId === defaultCategoryId) return;
    setCategoryId("");
    setTopicId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);
  useEffect(() => {
    setTopicId("");
  }, [categoryId]);

  const subjectName = useMemo(
    () => subjectsQuery.data?.find((s) => s.id === subjectId)?.name ?? "",
    [subjectsQuery.data, subjectId],
  );
  const categoryName = useMemo(
    () => categoriesQuery.data?.find((c) => c.id === categoryId)?.name ?? "",
    [categoriesQuery.data, categoryId],
  );
  const topicTitle = useMemo(
    () => topicsQuery.data?.find((t) => t.id === topicId)?.title ?? "",
    [topicsQuery.data, topicId],
  );

  const canSubmit = !!grade && !!subjectId && !!categoryId && !loading;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      grade,
      subjectId,
      subjectName,
      categoryId: categoryId || null,
      categoryName: categoryName || null,
      topicTitle: topicTitle || null,
      focusHint: focusHint.trim() || null,
      count,
      difficulty,
    });
  };

  return (
    <Card className="max-w-3xl mx-auto border-2 border-purple-200">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl">AI Practice Test</CardTitle>
            <CardDescription>
              Generate a fresh practice test tailored to grade, subject, and category.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {!user && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Free guest limit: <strong>5 AI generations per 6 hours</strong>.{" "}
              <a className="underline font-medium" href="/auth?mode=signup">
                Sign up free
              </a>{" "}
              for unlimited.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Grade</Label>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger>
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {GRADES.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g === "K" ? "Kindergarten" : `Grade ${g}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Subject</Label>
            <Select value={subjectId} onValueChange={setSubjectId} disabled={!grade}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !grade
                      ? "Pick a grade first"
                      : subjectsQuery.isLoading
                      ? "Loading…"
                      : "Select subject"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(subjectsQuery.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
                {subjectsQuery.data?.length === 0 && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No subjects for this grade.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId} disabled={!subjectId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    !subjectId
                      ? "Pick a subject first"
                      : categoriesQuery.isLoading
                      ? "Loading…"
                      : "Select category"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(categoriesQuery.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                {categoriesQuery.data?.length === 0 && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No categories for this subject.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Topic / subcategory (optional)</Label>
            <Select
              value={topicId || "__any__"}
              onValueChange={(v) => setTopicId(v === "__any__" ? "" : v)}
              disabled={!categoryId}
            >
              <SelectTrigger>
                <SelectValue placeholder={!categoryId ? "Pick a category first" : "Any topic"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any topic</SelectItem>
                {(topicsQuery.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="focus-hint">Focus hint (optional)</Label>
          <Input
            id="focus-hint"
            value={focusHint}
            onChange={(e) => setFocusHint(e.target.value)}
            placeholder="e.g. word problems involving fractions"
          />
        </div>

        <div>
          <Label>Number of questions: {count}</Label>
          <Slider
            value={[count]}
            min={5}
            max={20}
            step={1}
            onValueChange={(v) => setCount(v[0])}
            className="mt-3"
          />
        </div>

        <div>
          <Label>Difficulty</Label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {DIFFICULTIES.map((d) => (
              <Button
                key={d.id}
                type="button"
                variant={difficulty === d.id ? "default" : "outline"}
                onClick={() => setDifficulty(d.id)}
              >
                {d.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-between pt-2">
          {onCancel ? (
            <Button variant="ghost" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          ) : (
            <span />
          )}
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate &amp; Start
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
